import React from 'react';
import Head from 'next/head';
import Image from 'next/legacy/image';

import { BodyRegular, H1, H2, H3, H4 } from '@components/atoms/typography';
import Button from '@components/atoms/button';
import Icon from '@components/atoms/icon';
import ReviewsCarousel from '@components/reviews-carousel';
import { getPageSeoHead, PAGE_SEO } from '@lib/page-seo';

const PAGE_PATH = '/how-it-works/ultra';
const JOIN_PATH = '/join';
const YOUTUBE_EMBED_URL = 'https://www.youtube.com/embed/AiEEoG7Y8kA';
const EXPERTS_IMAGE = {
  src: '/images/how-it-works/ultra-experts.jpg',
  width: 300,
  height: 520
};

// Copied from the WordPress "How it works" template this page was built from
// until FFH-3299. It rarely changes, so it lives in code rather than Sanity.
const PAGE_TITLE =
  'Expert advice for your FPL team with the Fantasy Football Hub Ultra service.';
const PAGE_SUBTITLE =
  "Headed up by England's all-time number one FPL player Rich Clarke (regular Hub contributor and Green Arrow co-host), we have some of the very best coaches and content creators to help you gain an edge. Rich himself has nine top 10k finishes and two top 500 ranks in FPL and is the only player to have 17 top 50k finishes in the 17 year history of the game";
const JOIN_HEADER = 'Unlock the ultimate FPL advice service on the planet';
const JOIN_DESCRIPTION =
  'With our Ultra service, you can take your game to the next level. Become part of the most exclusive club of elite fantasy managers in the world.';
const FEATURES_HEADER = "What's included in Ultra?";
const FEATURES_DESCRIPTION = 'Join today and gain access to the following;';
const BOTTOM_JOIN_HEADER =
  'Dominate your FPL mini-leagues with the most advanced advice service in the game.';

const HOW_IT_WORKS_STEPS = [
  {
    header: 'Sign up to Ultra today',
    description:
      "You'll then be asked for some information about your team and you'll be allocated your own personal coach from our Ultra experts."
  },
  {
    header: 'Propel your team up the rankings',
    description:
      'Gain personalised, specific advice for your team via your monthly written team review'
  },
  {
    header: 'Access exclusive Ultra content',
    description: 'All designed to give you the ultimate edge on the opposition.'
  }
];

const FEATURES = [
  {
    iconName: 'Report',
    header: 'A monthly personalised review of your team',
    description:
      'Get specific advice for your team each month, from your dedicated Ultra coach.'
  },
  {
    iconName: 'LineGraph',
    header: "Unprecedented insight you'll not get anywhere else",
    description:
      "Our coaches are some of the highest-performing managers in the game. They'll provide strategies for the immediate and medium term, taking account of upcoming fixtures, player form, chips etc."
  },
  {
    iconName: 'WhatsApp',
    header: 'Exclusive access to a dedicated Ultra WhatsApp group',
    description:
      "Get tips from Hub Founder Will Thomas and England's All-time number 1 FPL Player, Rich Clarke"
  },
  {
    iconName: 'Group',
    header: 'Monthly Ultra Zoom call',
    description:
      'Attend for bonus content and Q&A with our panel of Ultra experts'
  },
  {
    iconName: 'PersonStar',
    header: "Invite to the Hub's 'End of Season' FPL event",
    description: 'Meet the experts in person.'
  },
  {
    iconName: 'Trophy',
    header: 'Access to our Ultra exclusive mini-league',
    description: 'With Trophy and Hub goodies for the winners.'
  }
] as const;

const UltraHowItWorks = () => (
  <>
    <Head>{getPageSeoHead(PAGE_PATH, PAGE_SEO.ultraHowItWorks)}</Head>
    <main className="bg-white space-y-5 md:space-y-10">
      <div className="container mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 py-10 mb-5">
          <div className="flex flex-col justify-center px-2 md:px-12">
            <H1 className="max-w-[520px] mb-3">{PAGE_TITLE}</H1>
            <BodyRegular className="max-w-[450px] mb-5">
              {PAGE_SUBTITLE}
            </BodyRegular>
            <Button href={JOIN_PATH} isLink variant="primary">
              Join The Hub
            </Button>
          </div>
          <div className="px-2 md:px-5 flex items-center">
            <iframe
              className="w-full aspect-video rounded-lg"
              src={YOUTUBE_EMBED_URL}
              title="Fantasy Football Hub Ultra"
            ></iframe>
          </div>
        </div>
        <div>
          <H3 className="text-center">How does it work?</H3>
          <div className="py-5 md:py-10 flex flex-col md:flex-row justify-evenly items-center md:items-start space-y-5 md:space-y-0">
            {HOW_IT_WORKS_STEPS.map(({ header, description }, index) => (
              <div
                key={header}
                className="flex flex-col space-y-4 justify-center max-w-[250px] items-center"
              >
                <H3
                  textColor="text-neutral-50"
                  className="flex items-center justify-center bg-primary-600 w-[48px] h-[48px] rounded-full"
                >
                  {index + 1}
                </H3>
                <H4 className="text-center">{header}</H4>
                <BodyRegular className="text-center">{description}</BodyRegular>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-primary-100 py-5 md:py-12 flex justify-center items-center flex-col px-5">
        <H2 className="mb-2">{JOIN_HEADER}</H2>
        <BodyRegular className="mb-7 text-center">
          {JOIN_DESCRIPTION}
        </BodyRegular>
        <Button href={JOIN_PATH} isLink variant="primary">
          Join The Hub
        </Button>
      </div>
      <div className="container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 py-10 px-2 md:px-8">
          <div className="flex justify-center">
            <Image
              width={EXPERTS_IMAGE.width}
              height={EXPERTS_IMAGE.height}
              src={EXPERTS_IMAGE.src}
              alt="The Ultra experts"
              objectFit="contain"
            />
          </div>
          <div className="flex flex-col space-y-8 px-5 md:px-10">
            <H2>{FEATURES_HEADER}</H2>
            <BodyRegular>{FEATURES_DESCRIPTION}</BodyRegular>
            {FEATURES.map(({ header, description, iconName }) => (
              <div key={header} className="flex flex-col">
                <div className="flex items-center mb-4">
                  <Icon className="w-6 h-6 mr-2" name={iconName} />
                  <H4>{header}</H4>
                </div>
                <BodyRegular className="pr-3 md:pr-10">
                  {description}
                </BodyRegular>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div>
        <ReviewsCarousel
          header="We make winning at fantasy football a reality"
          subHeader="Fantasy football is more popular than ever. But to combat the competition, you need an edge. Let our team of world-class experts guide your decisions every gameweek and start dominating your mini-leagues."
        />
      </div>
      <div className="flex flex-col items-center justify-center px-5 py-10 md:py-16 space-y-5">
        <H2 className="text-center">{BOTTOM_JOIN_HEADER}</H2>
        <Button href={JOIN_PATH} isLink variant="primary">
          Join The Hub today
        </Button>
      </div>
    </main>
  </>
);

export default UltraHowItWorks;
